import { useCallback } from 'react';
import qs from 'query-string';
import { useDispatch, useSelector } from 'react-redux';
import { push } from 'connected-react-router';

/**
 * @typedef {Record<string, string | string[]>} QueryParams - Record of query params stored
 */

/**
 * @callback QueryParamsSetterCallback
 * @param {QueryParams} oldQueryParams - The old query params
 * @returns {QueryParams} newQueryParams
 */

/**
 * @typedef {(callbackOrNewValues: QueryParamsSetterCallback | QueryParams) => void} QueryParamsSetter
 */

/**
  * @desc React hook to get and set query params. It requires redux and connected-react-router to update the url.
  * @returns {[QueryParams, QueryParamsSetter]} Returns an array with the query params object and a function to update them.
  *
  * @example
  * const [queryParams] = useQueryParams();
  * const { page } = queryParams;
  *
  * @example
  * const [queryParams, setQueryParams] = useQueryParams();
  * setQueryParams((oldQueryParams) => ({
  *   ...oldQueryParams,
  *   sort: 'name',
  *   order: 'asc',
  * });
  */
var useQueryParams = function useQueryParams() {
  var dispatch = useDispatch();
  var queryParamsString = useSelector(function (state) {
    return state.getIn(['router', 'location', 'search'], '');
  });
  var queryParams = qs.parse(queryParamsString, {
    arrayFormat: 'bracket'
  });

  /**
   * @type {QueryParamsSetter}
   */
  var setQueryParams = useCallback(function (callback) {
    var newValues = {};
    if (typeof callback === 'function') {
      newValues = callback(queryParams);
    } else {
      newValues = callback;
    }
    var search = qs.stringify(newValues, {
      arrayFormat: 'bracket'
    });
    dispatch(push({
      search: encodeURI(search)
    }));
  }, [dispatch, queryParams]);
  return [queryParams, setQueryParams];
};
export default useQueryParams;